import { Copy, Check, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import httpService from "../../../../services/httpService";
import {
  BasketDetail,
  PortfolioDetail,
  V3ApiResponse,
  VersionListItem,
} from "../../../../types/productV3";
import { formatDateCell } from "../helpers";

interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  productType: "BASKET" | "PORTFOLIO";
  providerId: number;
}

type ProductData = {
  title: string;
  description: string | null;
  visibility: string;
  createdAtUtc: string;
  raw: BasketDetail | PortfolioDetail;
};

const ADMIN_BASE = "/v3/products/providers";

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  isOpen,
  onClose,
  productId,
  productType,
  providerId,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [product, setProduct] = useState<ProductData | null>(null);
  const [versions, setVersions] = useState<VersionListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const productPath = productType === "BASKET" ? "baskets" : "portfolios";
      const base = `${ADMIN_BASE}/${providerId}/${productPath}/${productId}`;

      const [detailRes, versionsRes] = await Promise.all([
        httpService.get<V3ApiResponse<BasketDetail | PortfolioDetail>>(base),
        httpService.get<V3ApiResponse<VersionListItem[]>>(
          `${base}/versions?page=1&pageSize=50`
        ),
      ]);

      const detail = detailRes.data.data;
      const versionItems = versionsRes.data.data;

      if (productType === "BASKET") {
        const d = detail as BasketDetail;
        setProduct({
          title: d.basket.title,
          description: d.basket.description,
          visibility: d.basket.visibility,
          createdAtUtc: d.basket.createdAtUtc,
          raw: d,
        });
      } else {
        const d = detail as PortfolioDetail;
        setProduct({
          title: d.portfolio.title,
          description: d.portfolio.description,
          visibility: d.portfolio.visibility,
          createdAtUtc: d.portfolio.createdAtUtc,
          raw: d,
        });
      }
      setVersions(versionItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load product");
    } finally {
      setLoading(false);
    }
  }, [productId, productType, providerId]);

  useEffect(() => {
    if (isOpen) {
      setProduct(null);
      setVersions([]);
      setError(null);
      load();
    }
  }, [isOpen, load]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${
          isDarkMode ? "bg-rp-surface" : "bg-white"
        } rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${
            isDarkMode ? "border-rp-separator" : "border-gray-200"
          }`}
        >
          <div>
            {product && (
              <h2
                className={`text-lg font-bold ${
                  isDarkMode ? "text-rp-grey" : "text-white"
                }`}
              >
                {product.title}
              </h2>
            )}
            <div className="flex items-center gap-2 mt-0.5">
              <span
                className={`text-sm ${
                  isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                }`}
              >
                Provider {providerId}, {productType === "BASKET" ? "Basket" : "Portfolio"} {productId}
              </span>
              <button
                onClick={() => {
                  const text = `Provider ${providerId}, ${productType === "BASKET" ? "Basket" : "Portfolio"} ${productId}`;
                  navigator.clipboard.writeText(text);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className={`p-0.5 rounded ${
                  isDarkMode
                    ? "hover:bg-rp-surface-raised text-rp-grey/40"
                    : "hover:bg-gray-200 text-gray-400"
                }`}
                title="Copy to clipboard"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded-lg ${
              isDarkMode
                ? "hover:bg-rp-surface-raised text-rp-grey/50"
                : "hover:bg-gray-100 text-gray-500"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center h-32">
              <div
                className={`animate-spin rounded-full h-7 w-7 border-b-2 ${
                  isDarkMode ? "border-rp-amber" : "border-blue-600"
                }`}
              />
            </div>
          )}

          {error && (
            <div
              className={`p-4 rounded-lg ${
                isDarkMode
                  ? "bg-red-900/20 border border-red-800 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-700"
              }`}
            >
              <p className="text-sm">{error}</p>
            </div>
          )}

          {product && !loading && (
            <div className="space-y-5">
              {/* Product Info */}
              <div className="space-y-3">
                <InfoRow label="Title" value={product.title} isDarkMode={isDarkMode} />
                <InfoRow
                  label="Description"
                  value={product.description || "(none)"}
                  isDarkMode={isDarkMode}
                />
                <InfoRow
                  label="Visibility"
                  isDarkMode={isDarkMode}
                  value={
                    <span
                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        product.visibility === "PUBLIC"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {product.visibility}
                    </span>
                  }
                />
                <InfoRow
                  label="Created At"
                  value={formatDateCell(product.createdAtUtc)}
                  isDarkMode={isDarkMode}
                />
              </div>

              {/* Metadata JSON */}
              <div>
                <h3
                  className={`text-sm font-semibold mb-2 ${
                    isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                  }`}
                >
                  Metadata
                </h3>
                <pre
                  className={`text-xs p-3 rounded-lg overflow-x-auto max-h-48 ${
                    isDarkMode
                      ? "bg-rp-black text-rp-grey/70 border border-rp-separator"
                      : "bg-gray-50 text-gray-700 border border-gray-200"
                  }`}
                >
                  {JSON.stringify(product.raw, null, 2)}
                </pre>
              </div>

              {/* Versions */}
              <div>
                <h3
                  className={`text-sm font-semibold mb-2 ${
                    isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                  }`}
                >
                  Versions ({versions.length})
                </h3>
                {versions.length === 0 ? (
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-rp-grey/40" : "text-gray-400"
                    }`}
                  >
                    No versions published
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr
                          className={`text-left ${
                            isDarkMode
                              ? "text-rp-grey/40 border-b border-rp-separator"
                              : "text-gray-400 border-b border-gray-200"
                          }`}
                        >
                          <th className="pb-1.5 pr-3 font-medium">ID</th>
                          <th className="pb-1.5 pr-3 font-medium">Version</th>
                          <th className="pb-1.5 pr-3 font-medium">Status</th>
                          <th className="pb-1.5 pr-3 font-medium">Stocks</th>
                          {productType === "PORTFOLIO" && (
                            <>
                              <th className="pb-1.5 pr-3 font-medium">Options</th>
                              <th className="pb-1.5 pr-3 font-medium">Baskets</th>
                            </>
                          )}
                          <th className="pb-1.5 pr-3 font-medium">Published At</th>
                          <th className="pb-1.5 font-medium">Created At</th>
                        </tr>
                      </thead>
                      <tbody
                        className={`divide-y ${
                          isDarkMode ? "divide-rp-separator" : "divide-gray-100"
                        }`}
                      >
                        {versions.map((v) => (
                          <tr key={v.id}>
                            <td
                              className={`py-1.5 pr-3 font-mono ${
                                isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                              }`}
                            >
                              {v.id}
                            </td>
                            <td
                              className={`py-1.5 pr-3 font-mono ${
                                isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                              }`}
                            >
                              {v.version}
                            </td>
                            <td className="py-1.5 pr-3">
                              <span
                                className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                                  v.status === "published" || v.status === "ACTIVE"
                                    ? "bg-green-100 text-green-800"
                                    : v.status === "draft"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {v.status}
                              </span>
                            </td>
                            <td
                              className={`py-1.5 pr-3 text-center ${
                                isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                              }`}
                            >
                              {v.contracts.stocks}
                            </td>
                            {productType === "PORTFOLIO" && (
                              <>
                                <td
                                  className={`py-1.5 pr-3 text-center ${
                                    isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                  }`}
                                >
                                  {v.contracts.options ?? 0}
                                </td>
                                <td
                                  className={`py-1.5 pr-3 text-center ${
                                    isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                  }`}
                                >
                                  {v.baskets ?? 0}
                                </td>
                              </>
                            )}
                            <td
                              className={`py-1.5 pr-3 ${
                                isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                              }`}
                            >
                              {formatDateCell(v.publishedAtUtc)}
                            </td>
                            <td
                              className={`py-1.5 ${
                                isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                              }`}
                            >
                              {formatDateCell(v.createdAtUtc)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoRow: React.FC<{
  label: string;
  value: React.ReactNode;
  isDarkMode: boolean;
}> = ({ label, value, isDarkMode }) => (
  <div className="flex items-start">
    <span
      className={`text-xs w-24 flex-shrink-0 pt-0.5 ${
        isDarkMode ? "text-rp-grey/40" : "text-gray-400"
      }`}
    >
      {label}
    </span>
    <span className={`text-sm ${isDarkMode ? "text-rp-grey" : "text-gray-800"}`}>
      {value}
    </span>
  </div>
);

export default ProductDetailModal;
